import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { Institution } from '../models/institution';
import { ApiService } from './api.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class InstitutionService {
  private apiService = inject(ApiService);

  private institutionEventSubject = new BehaviorSubject<any>(null);
  public institutionEvent = this.institutionEventSubject.asObservable();

  public admin = {
    getInstitutions: this.getAdminInstitutions.bind(this),
    getInstitution: this.getAdminInstitution.bind(this),
    updateInstitution: this.updateAdminInstitution.bind(this),
    createInstitution: this.createAdminInstitution.bind(this),
    deleteInstitution: this.deleteAdminInstitution.bind(this),
  }

  private currentInstitutionSubject = new BehaviorSubject<Institution|null>(null);
  currentInstitution = this.currentInstitutionSubject.asObservable();

  setCurrentInstitutionGuid(guid: string): void {
    localStorage.setItem(environment.institutionKeyName, guid);
  }

  getInstitution(institutionId: any, query: any = {}): Observable<any> {
    return this.apiService.get(`secure/institutions/${institutionId}`, query);
  }

  getCurrentInstitution(): Observable<Institution> {
    return this.getInstitution('current', { include: 'specialties,settings,doctors,tests' })
      .pipe(
        map(
          (response) => {
            this.currentInstitutionSubject.next(response.data);
            return response.data;
          }
        )
      );
  }

  updateCurrentInstitution(institution: Institution): Observable<any> {
    return this.apiService.put(`secure/institutions/current`, institution);
  }

  getCurrentInstitutionSettings(query: any = {}): Observable<any> {
    return this.apiService.get(`secure/institutions/current/settings`, query);
  }

  updateCurrentInstitutionSettings(query: any = {}): Observable<any> {
    return this.apiService.post(`secure/institutions/current/settings`, query);
  }

  getInstitutionTemplates(query: any = {}): Observable<any> {
    return this.apiService.get(`secure/institutions/current/templates`, query);
  }

  getInstitutionTemplate(institutionTemplateId: number, query: any = {}): Observable<any> {
    return this.apiService.get(`secure/institutions/current/templates/${institutionTemplateId}`, query);
  }

  createInstitutionTemplates(query: any = {}): Observable<any> {
    return this.apiService.post(`secure/institutions/current/templates`, query);
  }

  updateInstitutionTemplates(institutionTemplateId: number, query: any = {}): Observable<any> {
    return this.apiService.put(`secure/institutions/current/templates/${institutionTemplateId}`, query);
  }

  deleteInstitutionTemplates(institutionTemplateId: number): Observable<any> {
    return this.apiService.delete(`secure/institutions/current/templates/${institutionTemplateId}`);
  }

  getInstitutionServices(query: any = {}): Observable<any> {
    return this.apiService.get(`secure/institutions/current/services`, query);
  }

  getInstitutionService(institutionServiceId: number): Observable<any> {
    return this.apiService.get(`secure/institutions/current/services/${institutionServiceId}`);
  }

  createInstitutionService(query: any = {}): Observable<any> {
    return this.apiService.post(`secure/institutions/current/services`, query);
  }

  updateInstitutionService(institutionServiceId: number, query: any = {}): Observable<any> {
    return this.apiService.put(`secure/institutions/current/services/${institutionServiceId}`, query);
  }

  deleteInstitutionService(institutionServiceId: number): Observable<any> {
    return this.apiService.delete(`secure/institutions/current/services/${institutionServiceId}`);
  }

  getInstitutionUsers(query: any = {}): Observable<any> {
    return this.apiService.get(`secure/institutions/current/users`, query);
  }

  getInstitutionUser(institutionUserId: number, query: any = {}): Observable<any> {
    return this.apiService.get(`secure/institutions/current/users/${institutionUserId}`, query);
  }

  createInstitutionUser(query: any = {}): Observable<any> {
    return this.apiService.post(`secure/institutions/current/users`, query);
  }

  updateInstitutionUser(institutionUserId: number, query: any = {}): Observable<any> {
    return this.apiService.put(`secure/institutions/current/users/${institutionUserId}`, query);
  }

  deleteInstitutionUser(institutionUserId: number): Observable<any> {
    return this.apiService.delete(`secure/institutions/current/users/${institutionUserId}`);
  }

  getInstitutionRoles(query: any = {}): Observable<any> {
    return this.apiService.get(`secure/roles`, query);
  }

  // #region Admin 


  private getAdminInstitutions(params: any = {}): Observable<any> {
    return this.apiService.get(`admin/institutions`, params);
  }

  private getAdminInstitution(institutionId: number, query: any = {}): Observable<any> {
    return this.apiService.get(`admin/institutions/${institutionId}`, query);
  }

  private updateAdminInstitution(institutionId: number, data: any): Observable<any> {
    return this.apiService.put(`admin/institutions/${institutionId}`, data);
  }

  private createAdminInstitution(data: any): Observable<any> {
    return this.apiService.post(`admin/institutions`, data);
  }

  private deleteAdminInstitution(institutionId: number): Observable<any> {
    return this.apiService.delete(`admin/institutions/${institutionId}`);
  }

  // #endregion

  // #region Reverb Events

  public subscribeToInstitution(institutionId: number): void {
    const LaravelEcho = (window as any)['Echo'];

    LaravelEcho
      .channel(`institutions.${institutionId}`)
      .listen(`.appointment-slots-created`, (data: any) => {
        this.institutionEventSubject.next({ type: 'appointment-slots-created', data });
      })
      .listen(`.appointment-slots-updated`, (data: any) => {
        this.institutionEventSubject.next({ type: 'appointment-slots-updated', data });
      })
      .listen(`.appointment-slots-deleted`, (data: any) => {
        this.institutionEventSubject.next({ type: 'appointment-slots-deleted', data });
      });
  }

  public unsubscribeFromInstitution(institutionId: number): void {
    const LaravelEcho = (window as any)['Echo'];

    LaravelEcho
      .leaveChannel(`institutions.${institutionId}`);
  }

  // #endregion
}
